import { GRAPH_NODE_MOVE, GRAPH_SESSION_PICK, GRAPH_SIGNAL_RESOLVED, GRAPH_TOOL_CALL, } from '../decision-logs/graph-session.js';
import { graphToolLabel } from './tool-labels.js';
/**
 * The prompt types the graph runtime writes to the session details run log,
 * one per kind of model call a turn makes. The admin labels them by name.
 */
export const GRAPH_RUN_TYPES = {
    SIGNAL_JUDGE: 'graph-signal-judge',
    WATCH_EVENTS: 'graph-watch-events',
    REPLY: 'graph-reply',
    TOOL_QUALIFIER: 'graph-tool-qualifier',
};
export const GRAPH_RUN_TYPE_LABELS = {
    [GRAPH_RUN_TYPES.SIGNAL_JUDGE]: 'Signal judge',
    [GRAPH_RUN_TYPES.WATCH_EVENTS]: 'Watch events',
    [GRAPH_RUN_TYPES.REPLY]: 'Reply',
    [GRAPH_RUN_TYPES.TOOL_QUALIFIER]: 'Tool qualifier',
};
/** The order the pipeline runs the steps of one turn; the admin lists a turn's rows in it. */
export const GRAPH_TURN_ORDER = [
    GRAPH_SESSION_PICK,
    GRAPH_RUN_TYPES.WATCH_EVENTS,
    GRAPH_RUN_TYPES.SIGNAL_JUDGE,
    GRAPH_RUN_TYPES.TOOL_QUALIFIER,
    GRAPH_TOOL_CALL,
    GRAPH_SIGNAL_RESOLVED,
    GRAPH_NODE_MOVE,
    GRAPH_RUN_TYPES.REPLY,
];
/** Where a run or decision type sits in the turn; unknown types sort last. */
export function graphTurnStep(type) {
    const index = GRAPH_TURN_ORDER.indexOf(type);
    return index === -1 ? GRAPH_TURN_ORDER.length : index;
}
export const GRAPH_REPLY_SUMMARY_LENGTH = 120;
function text(value) {
    return typeof value === 'string' && value.length > 0 ? value : null;
}
function count(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function dollars(value) {
    return `$${value.toLocaleString('en-US')}`;
}
export function clip(value, length) {
    const line = value.replace(/\s+/g, ' ').trim();
    return line.length > length ? `${line.slice(0, length).trimEnd()}…` : line;
}
/** The bedrooms, rent, area and pets a listings request names, as one phrase. */
export function listingsCriteria(request) {
    const min = count(request?.minBedrooms);
    const max = count(request?.maxBedrooms);
    const rent = count(request?.maxMonthlyRent);
    const area = text(request?.area);
    const pets = text(request?.pets);
    const beds = min !== null && max !== null
        ? min === max
            ? `${min} beds`
            : `${min}–${max} beds`
        : min !== null
            ? `${min}+ beds`
            : max !== null
                ? `up to ${max} beds`
                : 'any beds';
    const petsPhrase = pets === 'none'
        ? 'no pets'
        : pets === 'dog' || pets === 'cat'
            ? `a ${pets}`
            : 'pets unspecified';
    return [
        beds,
        rent !== null ? `under ${dollars(rent)}` : 'any rent',
        area ? `in ${area}` : 'any area',
        petsPhrase,
    ].join(', ');
}
const SIGNAL_READINGS = {
    yes: 'yes',
    no: 'no',
    not_established: 'not established',
};
export const GRAPH_RUN_SUMMARIES = {
    [GRAPH_RUN_TYPES.SIGNAL_JUDGE]: (input, output) => {
        const signal = text(input?.signal) ?? 'signal';
        const answer = text(output?.value);
        return `${signal}: ${answer ? (SIGNAL_READINGS[answer] ?? answer) : 'no reading'}`;
    },
    [GRAPH_RUN_TYPES.WATCH_EVENTS]: (_input, output) => {
        const detected = Array.isArray(output?.detected)
            ? output.detected.filter((name) => typeof name === 'string')
            : [];
        return detected.length > 0
            ? `Raised: ${detected.join(', ')}`
            : 'No events raised';
    },
    [GRAPH_RUN_TYPES.TOOL_QUALIFIER]: (input, output) => {
        const label = graphToolLabel(input?.tool);
        if (output?.applies !== true)
            return `${label}: does not apply`;
        if (label === 'Language') {
            return `${label}: switch to ${text(output.language) ?? 'unknown'}`;
        }
        return `${label}: applies. ${listingsCriteria(output)}`;
    },
    [GRAPH_RUN_TYPES.REPLY]: (_input, output) => {
        const reply = text(output?.reply) ?? text(output?.content);
        return reply ? clip(reply, GRAPH_REPLY_SUMMARY_LENGTH) : 'No reply text';
    },
};
export function isGraphRunType(value) {
    return Object.values(GRAPH_RUN_TYPES).includes(value);
}
//# sourceMappingURL=run-types.js.map